import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell, BellRing, Check, Mail, X } from 'lucide-react';
import { fetchVenues, toggleAlert, sendTestAlertEmail, UserProfile, Venue } from '../services/api';

interface Props {
  userProfile: UserProfile | null;
  setUserProfile: React.Dispatch<React.SetStateAction<UserProfile | null>>;
}

const AlertSettings: React.FC<Props> = ({ userProfile, setUserProfile }) => {
  const [venues, setVenues] = useState<Venue[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testMessage, setTestMessage] = useState('');
  const [testError, setTestError] = useState('');
  const [saving, setSaving] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const profileId = userProfile?.id;
  const load = useCallback(async () => {
    if (!profileId) { setLoading(false); return; }
    setLoading(true);
    setLoadError(false);
    try { setVenues(await fetchVenues()); }
    catch { setLoadError(true); }
    finally { setLoading(false); }
  }, [profileId]);
  useEffect(() => { load(); }, [load]);

  const selectedIds = userProfile?.alert_venue_ids ?? [];
  const selected = venues.filter(venue => selectedIds.includes(venue.id));
  const sendTest = async () => {
    setTesting(true);
    setTestMessage('');
    setTestError('');
    try { setTestMessage((await sendTestAlertEmail()).detail); }
    catch (err: any) { setTestError(err?.response?.data?.detail || "We couldn't send the test email. Please try again."); }
    finally { setTesting(false); }
  };

  const change = async (venue: Venue, enabled: boolean) => {
    if (saving !== null) return;
    setSaving(venue.id);
    setError('');
    setNotice('');
    try {
      const updated = await toggleAlert(venue.id, enabled);
      setUserProfile(previous => previous ? { ...previous, alert_venue_ids: updated.alert_venue_ids } : previous);
      setNotice(enabled ? `Alerts enabled for ${venue.name}. Look out for availability after the next check.` : `Alerts turned off for ${venue.name}.`);
    } catch (err: any) {
      setError(err?.response?.data?.detail || "We couldn't save that change. Please try again.");
    } finally { setSaving(null); }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center gap-2 text-clay text-xs font-semibold uppercase tracking-widest mb-4"><Bell size={16} /> Eatsy Pro</div>
      <h1 className="font-display text-4xl sm:text-5xl tracking-tight mb-4">Your email alerts</h1>
      <p className="text-ink-600 leading-relaxed max-w-xl mb-8">We'll watch your chosen restaurants and email you when a check finds tables you haven't heard about yet. Manage your 5 selections here.</p>

      {!userProfile?.is_pro && (
        <div className="rounded-2xl bg-brand-600 text-white p-6 mb-6">
          <h2 className="font-display text-2xl mb-2">A little help finding a memorable meal.</h2>
          <p className="text-brand-100 text-sm leading-relaxed mb-4">Restaurant alerts are included with Pro. {selectedIds.length > 0 ? 'Your alerts are paused while your Pro access is inactive. You can still remove restaurants below.' : 'Pick your favourites, then let us keep an eye on availability.'}</p>
          <Link to="/availability" className="inline-flex rounded-full bg-surface text-brand-700 px-5 py-2.5 font-medium text-sm">{userProfile ? 'Get Pro access' : 'Sign in and explore Pro'}</Link>
        </div>
      )}

      {userProfile && (
        <>
          <section aria-labelledby="selected-alerts" className="rounded-2xl border border-ink-200 bg-surface p-5 sm:p-7 mb-8">
            <div className="flex justify-between items-center gap-3 mb-2">
              <h2 id="selected-alerts" className="font-display text-2xl">Your restaurants</h2>
              <span className="rounded-full bg-brand-100 text-brand-800 px-3 py-1 text-sm whitespace-nowrap">{selectedIds.length} / 5 selected</span>
            </div>
            <p className="text-sm text-ink-600 mb-5 break-words">{userProfile.user.email ? <>Delivered to <strong className="font-medium">{userProfile.user.email}</strong></> : 'Your account needs an email address before you can enable alerts.'}</p>
            {loading ? <p role="status" className="text-sm text-ink-500">Loading your restaurants…</p> : loadError ? (
              <div role="alert" className="text-sm text-red-700">We couldn't load your restaurants. <button onClick={load} className="underline">Try again</button></div>
            ) : selected.length === 0 ? (
              <div className="rounded-xl border border-dashed border-brand-200 bg-brand-50 px-4 py-7 text-center">
                <BellRing className="mx-auto mb-3 text-brand-500" size={24} />
                <p className="font-medium">Your favourite tables start here.</p>
                <p className="text-sm text-ink-600 mt-1">Find a restaurant with no tables on Availability, then choose Alert me.</p>
              </div>
            ) : (
              <ul className="divide-y divide-ink-100">
                {selected.map(venue => (
                  <li key={venue.id} className="flex items-center justify-between gap-4 py-4">
                    <div className="min-w-0"><h3 className="font-display text-lg">{venue.name}</h3><p className="text-xs text-ink-500 mt-1">{venue.city} · {!venue.tracks_availability ? 'Availability checks paused' : userProfile.is_pro ? 'Alerts on' : 'Paused until you renew Pro'}</p></div>
                    <button type="button" onClick={() => change(venue, false)} disabled={saving !== null} aria-label={`Remove alerts for ${venue.name}`} className="shrink-0 flex items-center gap-1 rounded-full border border-ink-200 px-3 py-2 text-xs text-ink-600 hover:bg-ink-50 disabled:opacity-50"><X size={14} />{saving === venue.id ? 'Saving…' : 'Remove'}</button>
                  </li>
                ))}
              </ul>
            )}
            <p role="status" className="text-sm text-brand-700 mt-3">{notice}</p>
            {error && <p role="alert" className="text-sm text-red-700 mt-3">{error}</p>}
          </section>

          <Link to="/availability?view=alerts" className="inline-flex rounded-full bg-brand-600 px-5 py-3 text-sm font-medium text-white">Find restaurants to watch →</Link>
          {userProfile.is_pro && (
            <section aria-labelledby="test-alert-email" className="rounded-2xl border border-ink-200 bg-surface p-5 sm:p-7 mt-8">
              <h2 id="test-alert-email" className="font-display text-2xl mb-2">Check your inbox connection</h2>
              <p className="text-sm text-ink-600 mb-4 break-words">Send a sample alert to {userProfile.user.email}. It uses example times so you can check the email without waiting for a table to open. Your saved alerts stay the same.</p>
              <button type="button" onClick={sendTest} disabled={testing || !userProfile.user.email} className="inline-flex items-center gap-2 rounded-full border border-brand-200 px-4 py-2.5 text-sm text-brand-700 disabled:opacity-50"><Mail size={16} />{testing ? 'Sending…' : 'Send a test email'}</button>
              {testMessage && <p role="status" className="mt-3 text-sm text-brand-700">{testMessage}</p>}
              {testError && <p role="alert" className="mt-3 text-sm text-red-700">{testError}</p>}
            </section>
          )}
        </>
      )}
      <div className="mt-8 border-t border-ink-200 pt-6 text-sm text-ink-600 space-y-3">
        <p className="flex gap-2"><Check size={16} className="shrink-0 text-brand-500 mt-0.5" />Only new information: we won't repeat a sitting we've already emailed you.</p>
        <p className="flex gap-2"><Check size={16} className="shrink-0 text-brand-500 mt-0.5" />Dates, times, party sizes and booking links, all in your email.</p>
        <p className="text-xs leading-relaxed">Alerts arrive after scheduled checks. Availability can change before you book. Remove a restaurant here anytime to stop its alerts.</p>
      </div>
    </div>
  );
};

export default AlertSettings;
